import { APP_LOGIN_URL } from "../lib/config";
import { usePageMetadata } from "../lib/metadata";

export function HomePage() {
  usePageMetadata({
    title: "Emails Unsubscriber — unsubscribe from email in bulk",
    description: "Group recurring Gmail subscriptions, select the ones you do not want, and start every unsubscribe at once.",
  });

  return (
    <>
      <section className="unsub-hero">
        <div className="unsub-shell unsub-hero-grid">
          <div>
            <p className="unsub-eyebrow">Dozens of subscriptions. One cleanup pass.</p>
            <h1>An unsubscribe button for your whole inbox.</h1>
            <p className="unsub-hero-copy">
              Review recurring Gmail subscriptions in one list, select everything you do not
              want, and launch the whole cleanup at once. One-click links run automatically;
              anything else opens ready for your final click.
            </p>
            <div className="unsub-hero-actions">
              <a className="unsub-button" href={APP_LOGIN_URL}>Connect Gmail <span aria-hidden="true">→</span></a>
              <span className="unsub-beta-note">
                5 unsubscribes free · then $2.99/month<br />
                Google verification is not complete
              </span>
            </div>
          </div>

          <div className="unsub-product-shot" aria-label="Preview of selecting recurring subscriptions in Emails Unsubscriber">
            <div className="unsub-app-card">
              <div className="unsub-app-top">
                <span className="unsub-app-title">Review subscriptions</span>
                <span className="unsub-app-count">42 found</span>
              </div>
              <div className="unsub-app-list">
                <PreviewRow name="Daily Dispatch" email="hello@dailydispatch.example" count="96 emails" />
                <PreviewRow name="That store you tried once" email="offers@store.example" count="41 emails" />
                <PreviewRow name="Weekly Product News" email="newsletter@product.example" count="28 emails" />
              </div>
              <div className="unsub-bulk-preview">
                <span className="unsub-selected-label">3 subscriptions selected</span>
                <span className="unsub-fake-action">Unsubscribe selected (3)</span>
              </div>
              <div className="unsub-result-preview">2 automatic · 1 opened in a tab</div>
            </div>
          </div>
        </div>
      </section>

      <section className="unsub-truth-strip" aria-label="Product capabilities">
        <div className="unsub-shell unsub-truth-grid">
          <Fact label="Fast">Review dozens at once instead of opening emails one by one.</Fact>
          <Fact label="Actionable">Only email with a usable unsubscribe route enters your review list.</Fact>
          <Fact label="Complete">If one-click cannot finish it, Emails Unsubscriber opens the exact page you need.</Fact>
        </div>
      </section>

      <section className="unsub-section" id="how-it-works">
        <div className="unsub-shell">
          <div className="unsub-section-heading">
            <div className="unsub-section-label">The whole workflow</div>
            <h2>Three mechanical steps. No inbox archaeology.</h2>
          </div>
          <div className="unsub-steps">
            <Step number="01" title="Scan Gmail">Emails Unsubscriber works through Gmail in quota-safe batches and remembers where the scan stopped.</Step>
            <Step number="02" title="Choose subscriptions">Review recurring subscriptions by sender, subject, and message count. Keep the useful ones.</Step>
            <Step number="03" title="Unsubscribe in bulk">Verified one-click links run automatically. Every other unsubscribe page opens for you.</Step>
          </div>
        </div>
      </section>

      <section className="unsub-section unsub-pricing" id="pricing">
        <div className="unsub-shell">
          <div className="unsub-section-heading">
            <div className="unsub-section-label">Simple pricing</div>
            <h2>Try the real workflow before paying.</h2>
          </div>
          <div className="unsub-price-grid">
            <article className="unsub-price-card">
              <div className="unsub-price-name">Free</div>
              <div className="unsub-price"><strong>$0</strong></div>
              <p>Connect Gmail, review your first page, and complete your first five unsubscribes.</p>
              <ul><li>Mailbox scanning</li><li>Subscription grouping</li><li>5 completed unsubscribes</li></ul>
            </article>
            <article className="unsub-price-card unsub-price-featured">
              <div className="unsub-price-badge">Full cleanup</div>
              <div className="unsub-price-name">Unlimited</div>
              <div className="unsub-price"><strong>$2.99</strong><span>/ month</span></div>
              <p>Unlock every review page and unsubscribe without a usage cap.</p>
              <ul><li>Every subscription page</li><li>Unlimited individual actions</li><li>Unlimited bulk actions</li></ul>
              <a className="unsub-button" href={APP_LOGIN_URL}>Start cleaning <span aria-hidden="true">→</span></a>
            </article>
          </div>
        </div>
      </section>

      <section className="unsub-section unsub-privacy" id="privacy">
        <div className="unsub-shell unsub-privacy-card">
          <h2>Your inbox is not the product.</h2>
          <div className="unsub-privacy-copy">
            <p><strong>Read-only Gmail access.</strong> Emails Unsubscriber finds subscription identity and unsubscribe routes; it cannot send or delete your email.</p>
            <p><strong>An honest warning.</strong> Google has not approved this OAuth app, so Google will show an extra security warning during connection.</p>
          </div>
        </div>
      </section>

      <section className="unsub-closing">
        <div className="unsub-shell">
          <h2>Make the noisy part of your inbox smaller.</h2>
          <p>Start free. Subscribe only if there is more cleanup worth doing.</p>
          <a className="unsub-button" href={APP_LOGIN_URL}>Open Emails Unsubscriber <span aria-hidden="true">→</span></a>
        </div>
      </section>
    </>
  );
}

function PreviewRow({ name, email, count }: { name: string; email: string; count: string }) {
  return (
    <div className="unsub-app-row">
      <span className="unsub-fake-check" aria-hidden="true">✓</span>
      <span className="unsub-sender"><strong>{name}</strong><span>{email}</span></span>
      <span className="unsub-mail-count">{count}</span>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="unsub-truth"><div className="unsub-truth-label">{label}</div><p>{children}</p></div>;
}

function Step({ number, title, children }: { number: string; title: string; children: React.ReactNode }) {
  return <article className="unsub-step"><div className="unsub-step-number">{number}</div><h3>{title}</h3><p>{children}</p></article>;
}
